import Hero from '../../components/Hero';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { getContent } from '../../lib/api';
import type { ContentBundle } from '../../lib/types';
import { pick } from '../../lib/types';
import { timeWithZone } from '../../lib/time';
import { useT } from '../../i18n/LangProvider';

export default function HomePage() {
  const [content, setContent] = useState<ContentBundle | null>(null);
  const [teamsLink, setTeamsLink] = useState<string>('');
  const { t, lang } = useT();
  useEffect(() => { getContent().then(setContent).catch(() => {}); }, []);
  useEffect(() => { fetch('/api/reg-config').then((r) => r.json()).then((c) => setTeamsLink(c.teamsLink || '')).catch(() => {}); }, []);
  const featured = content?.sessions.slice(0, 3) ?? [];
  const shortDate = (iso: string) =>
    new Date(iso).toLocaleDateString(lang === 'fr' ? 'fr-CA' : 'en-CA', { month: 'short', day: 'numeric' });

  return (
    <>
      <Hero />

      {/* WHAT */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-20 pt-12 pb-12 grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-10 md:col-start-2">
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.7 }} className="max-w-3xl">
            <p className="text-sm tracking-[0.2em] uppercase text-brand-red/90 mb-4 font-mono font-medium">{t('what.kicker')}</p>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight text-ink mb-6">{t('what.title')}</h2>
            <p className="text-ink-muted text-lg leading-relaxed">{t('what.body')}</p>
          </motion.div>
        </div>
      </section>

      {/* WHEN */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-20 py-12 grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-10 md:col-start-2">
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.7 }}>
            <p className="text-sm tracking-[0.2em] uppercase text-brand-red/90 mb-4 font-mono font-medium">{t('when.kicker')}</p>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight text-ink mb-8">{t('when.title')}</h2>
            <div className="space-y-4 max-w-2xl">
              {[t('when.arrival'), t('when.programStart'), t('when.programEnd'), t('when.inPersonEnd')].map((line, i) => (
                <div key={i} className="flex items-start gap-4 card p-5">
                  <span className="w-2 h-2 rounded-full bg-brand-red mt-2 flex-shrink-0" />
                  <p className="text-ink text-[15px]">{line}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* WHERE */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-20 py-12 grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-10 md:col-start-2">
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.7 }}>
            <p className="text-sm tracking-[0.2em] uppercase text-brand-red/90 mb-8 font-mono font-medium">{lang === 'fr' ? '// Lieu' : '// Where'}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {/* In person */}
              <div>
                <div className="text-center mb-4 min-h-[4.5rem] flex flex-col justify-start">
                  <h2 className="font-display text-2xl md:text-3xl tracking-tight text-ink">Centre Mont-Royal</h2>
                  <a href="https://www.google.com/maps/search/?api=1&query=Centre+Mont-Royal%2C+2200+Mansfield+St%2C+Montr%C3%A9al%2C+QC+H3A+3R8" target="_blank" rel="noopener noreferrer" className="inline-block text-sm text-ink-muted hover:text-brand-red transition-colors">2200 Mansfield St., Montréal, QC H3A 3R8 →</a>
                </div>
                <p className="text-center text-brand-red text-sm mb-3 font-mono uppercase tracking-wider">{lang === 'fr' ? 'En personne' : 'In person'}</p>
                <div className="rounded-2xl overflow-hidden border border-white/[0.08] aspect-[4/3] bg-bg-elev">
                  <img src="/venue-inperson.jpg" alt="Centre Mont-Royal" className="w-full h-full object-cover" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
                </div>
              </div>
              {/* Online */}
              <div>
                <div className="text-center mb-4 min-h-[4.5rem] flex flex-col justify-start">
                  <h2 className="font-display text-2xl md:text-3xl tracking-tight text-ink">{lang === 'fr' ? 'Diffusion en direct sur Teams' : 'Livestream on Teams'}</h2>
                  {teamsLink
                    ? <a href={teamsLink} target="_blank" rel="noopener noreferrer" className="inline-block text-sm text-ink-muted hover:text-brand-red transition-colors mt-1">{lang === 'fr' ? 'Regarder en direct →' : 'Watch live →'}</a>
                    : <p className="text-ink-muted text-sm mt-1">{lang === 'fr' ? 'Lien de l\'événement à venir' : 'Event link coming soon'}</p>}
                </div>
                <p className="text-center text-brand-red text-sm mb-3 font-mono uppercase tracking-wider">{lang === 'fr' ? 'En ligne' : 'Online'}</p>
                <div className="rounded-2xl overflow-hidden border border-white/[0.08] aspect-[4/3] bg-bg-elev">
                  <img src="/venue-online.jpg" alt={lang === 'fr' ? 'En ligne' : 'Online'} className="w-full h-full object-cover" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
                </div>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden border border-white/[0.08] h-[400px]">
              <iframe
                title="Centre Mont-Royal map"
                width="100%" height="100%" style={{ border: 0, filter: 'invert(0.9) hue-rotate(180deg)' }}
                loading="lazy" referrerPolicy="no-referrer-when-downgrade"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2796.3678755950295!2d-73.57735322372393!3d45.50267267107458!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4cc91a46c4fd8faf%3A0x8ffa34cff99801de!2sMount%20Royal%20Center!5e0!3m2!1sen!2sca!4v1753721976097!5m2!1sen!2sca"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* WHO */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-20 py-12 grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-10 md:col-start-2">
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.7 }} className="max-w-3xl">
            <p className="text-sm tracking-[0.2em] uppercase text-brand-red/90 mb-4 font-mono font-medium">{t('who.kicker')}</p>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight text-ink mb-6">{t('who.title')}</h2>
            <p className="text-ink-muted text-lg leading-relaxed">{t('who.body')}</p>
          </motion.div>
        </div>
      </section>

      {/* ENGAGE */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-20 py-12 grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-10 md:col-start-2">
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.7 }} className="mb-10 max-w-2xl">
            <p className="text-sm tracking-[0.2em] uppercase text-brand-red/90 mb-4 font-mono font-medium">{t('engage.kicker')}</p>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight text-ink">{t('engage.title')}</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <motion.article initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.6 }} className="card p-7">
              <p className="font-display text-2xl tracking-tight text-ink mb-3">{t('engage.videoTitle')}</p>
              <p className="text-ink-muted text-[15px] leading-relaxed mb-5">{t('engage.videoBody')}</p>
              <a href={`mailto:${t('engage.videoCta')}`} className="text-sm text-brand-red hover:underline font-mono">{t('engage.videoCta')} →</a>
            </motion.article>

            <motion.article initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.6, delay: 0.1 }} className="card p-7">
              <p className="font-display text-2xl tracking-tight text-ink mb-3">{t('engage.questionTitle')}</p>
              <p className="text-ink-muted text-[15px] leading-relaxed mb-5">{t('engage.questionBody')}</p>
              <a href={`mailto:${t('engage.questionCta')}`} className="text-sm text-brand-red hover:underline font-mono">{t('engage.questionCta')} →</a>
            </motion.article>

            <motion.article initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.6, delay: 0.2 }} className="card p-7">
              <p className="font-display text-2xl tracking-tight text-ink mb-3">{t('engage.pollTitle')}</p>
              <p className="text-ink-muted text-[15px] leading-relaxed mb-4">{t('engage.pollBody')}</p>
              <Link to="/poll" className="text-sm text-brand-red hover:underline font-mono">{t('poll.goToPoll')}</Link>
            </motion.article>
          </div>

          <p className="text-ink-muted text-sm mt-8">{t('engage.contact').split('connect@tecsys.com')[0]}<a href="mailto:connect@tecsys.com" className="text-brand-red hover:underline">connect@tecsys.com</a>{t('engage.contact').split('connect@tecsys.com')[1] || ''}</p>
        </div>
      </section>

{/* Featured sessions */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-20 py-20 grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-10 md:col-start-2">
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.7 }} className="flex items-baseline justify-between flex-wrap gap-3 mb-12">
            <h2 className="font-display text-4xl md:text-5xl tracking-tight text-ink">{lang === 'fr' ? 'Séances en vedette' : 'Featured sessions'}</h2>
            <Link to="/agenda" className="text-sm text-brand-red lowercase hover:underline">{lang === 'fr' ? 'Voir le programme complet →' : 'view full agenda →'}</Link>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {(featured.length ? featured : [null, null, null]).map((s, i) => (
              <motion.article key={s?.id ?? i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.6, delay: i * 0.08 }} className="card p-6 hover:-translate-y-1 transition-all duration-300">
                <p className="text-[10px] tracking-[0.18em] uppercase text-brand-red mb-3 font-mono">{lang === 'fr' ? 'Séance' : 'Session'}</p>
                <h3 className="font-display text-xl text-ink leading-tight mb-3">{s ? pick(lang, s.TitleEn, s.TitleFr, s.Title) : (lang === 'fr' ? 'À venir bientôt' : 'Coming soon')}</h3>
                {s && pick(lang, s.DescriptionEn, s.DescriptionFr, s.Description) && <p className="text-sm text-ink-muted line-clamp-3">{pick(lang, s.DescriptionEn, s.DescriptionFr, s.Description)}</p>}
                <div className="mt-5 pt-4 border-t border-white/[0.07] text-xs text-ink-muted font-mono">
                  {s?.StartTime ? `${shortDate(s.StartTime)}, ${timeWithZone(s.StartTime, lang)} · ${s.Room || 'TBA'}` : (lang === 'fr' ? 'Horaire en cours de finalisation' : 'Agenda being finalized')}
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>


      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-20 pb-32 grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-10 md:col-start-2">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.7 }} className="relative card p-10 md:p-16 overflow-hidden">
            <div className="absolute -top-1/2 -right-1/4 w-[600px] h-[600px] rounded-full bg-brand-red/20 blur-3xl pointer-events-none" />
            <div className="relative max-w-2xl">
              <h2 className="font-display text-4xl md:text-6xl tracking-tight leading-[1.02] text-ink">{t('cta.title1')}<br /><span className="text-ink-muted">{t('cta.title2')}</span></h2>
              <p className="text-ink-muted mt-5 max-w-md">{t('cta.body')}</p>
              <Link to="/register" className="inline-flex items-center gap-2 bg-brand-red text-white rounded-md px-6 py-3 mt-8 hover:bg-brand-red-soft transition-colors text-sm">{t('cta.button')} <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg></Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
